// Game mode + special rounds + start, for a bot that's already been picked.
// Used by Home's "Play vs …" sheet and the /solo page.
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Zap, Trophy, Shuffle } from "lucide-react";
import useSoloStore, { BOT_CONFIGS } from "../stores/soloStore";
import { assignSpecialRounds } from "../Services/soloSpecialRounds";

const ACTIVE_PHASES = ["playing", "reviewing", "round_results"];

const ModeButton = ({ active, onClick, icon, title, subtitle }) => (
  <button
    onClick={onClick}
    aria-pressed={active}
    className={`flex-1 py-4 rounded-2xl border-2 transition-all text-left px-4 ${
      active ? "border-red-400 bg-red-50" : "border-transparent bg-gray-50 hover:bg-gray-100"
    }`}
  >
    <div className="flex items-center gap-1.5 mb-1">
      {React.createElement(icon, { size: 14, className: active ? "text-red-500" : "text-gray-500" })}
      <span className={`font-bold text-sm ${active ? "text-red-600" : "text-gray-600"}`}>{title}</span>
    </div>
    <p className="text-xs text-gray-600">{subtitle}</p>
  </button>
);

export default function SoloGameOptions({ bot }) {
  const navigate = useNavigate();
  const { startGame, gamePhase, selectedBot: activeBot, currentRound, totalRounds, resetGame } = useSoloStore();
  const [gameMode, setGameMode] = useState("quick");
  const [enableSpecialRounds, setEnableSpecialRounds] = useState(false);
  const hasActiveGame = ACTIVE_PHASES.includes(gamePhase);

  function handleStart() {
    const rounds = gameMode === "quick" ? 5 : 26;
    const specialRounds = enableSpecialRounds ? assignSpecialRounds(rounds, gameMode) : [];
    startGame(bot, gameMode, enableSpecialRounds, specialRounds);
    navigate("/solo/game");
  }

  return (
    <div className="space-y-4">
      {hasActiveGame && (
        <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-4 flex items-center justify-between gap-3">
          <div>
            <p className="text-sm font-bold text-gray-800">Game in progress</p>
            <p className="text-xs text-gray-500 mt-0.5">
              Round {currentRound} of {totalRounds} vs {BOT_CONFIGS[activeBot]?.name}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button onClick={() => resetGame()} className="min-h-[44px] px-2 text-xs text-gray-600 hover:text-red-600 transition-colors font-medium">
              Discard
            </button>
            <button
              onClick={() => navigate("/solo/game")}
              className="btn-press bg-red-600 text-white text-xs font-bold px-3 py-2 rounded-xl hover:bg-red-700"
            >
              Continue →
            </button>
          </div>
        </div>
      )}

      <div>
        <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-2">Game Mode</p>
        <div className="flex gap-2">
          <ModeButton active={gameMode === "quick"} onClick={() => setGameMode("quick")} icon={Zap} title="Quick" subtitle="5 rounds" />
          <ModeButton active={gameMode === "full"} onClick={() => setGameMode("full")} icon={Trophy} title="Full Game" subtitle={BOT_CONFIGS[bot]?.rewardOnWin > 0 ? `26 rounds · win ${BOT_CONFIGS[bot].rewardOnWin} coins` : "26 rounds, A to Z"} />
        </div>
      </div>

      <div className="rounded-2xl bg-gray-50 px-4 py-3.5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-white flex items-center justify-center flex-shrink-0">
            <Shuffle size={15} className="text-gray-500" />
          </div>
          <div>
            <p className="text-sm font-bold text-gray-800">Special Rounds</p>
            <p className="text-xs text-gray-600">Random bonuses and penalties</p>
          </div>
        </div>
        <button
          role="switch"
          aria-checked={enableSpecialRounds}
          aria-label="Special rounds"
          onClick={() => setEnableSpecialRounds((v) => !v)}
          className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${
            enableSpecialRounds ? "bg-red-500" : "bg-gray-300"
          }`}
        >
          <span
            className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
              enableSpecialRounds ? "translate-x-5" : "translate-x-0"
            }`}
          />
        </button>
      </div>

      <button onClick={handleStart} className="btn-press w-full py-4 rounded-2xl font-bold text-lg text-white bg-red-600 hover:bg-red-700">
        Start Game vs {BOT_CONFIGS[bot]?.name}
      </button>
    </div>
  );
}
